import cn from "classnames";
import { useEffect, useState, memo, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { AuthService, websiteConstructorApi } from "@rshbintech.dboul/http";
import {
  Text,
  Button,
  Tooltip,
  useMediaQuery,
} from "@rshbintech.dboul/core-ui";
import {
  Plus as PlusIcon,
  OwnFarm as OwnFarmingIcon,
} from "@rshbintech.dboul/icons";
import { useBankForBanksIndication } from "@rshbintech.dboul/access-control";
import { useAppTranslation, useToggle } from "../../hooks";
import { OwnFarmingDialog } from "./own-farming-dialog";
import { MenuItem } from "./menu-item";
import { OWN_FARMING_MENU_ITEM_KEY } from "./constants";
import { MenuProps, MenuItem as MenuItemType } from "./types";
import { ExpandableMenuItem } from "./expandable-menu-item";
import styles from "./styles.scss";

export const Menu = memo(
  ({
    isOpen,
    menuConfig,
    clientBlocks,
    clientSettings,
    onMenuItemClick,
  }: MenuProps) => {
    const navigate = useNavigate();
    const { translate } = useAppTranslation("MENU");

    const isAvailableCreatePermission = useMemo(
      () => !clientBlocks?.orgBlocks?.length,
      [clientBlocks?.orgBlocks?.length]
    );

    const { isBankForBanksUser } = useBankForBanksIndication();

    const [isSaasMenuAvailable, setIsSaasMenuAvailable] = useState(false);

    const [isOwnFarmingMenuItemShow, setIsOwnFarmingMenuItemShow] =
      useState(false);

    const [isTooltipVisible, setIsTooltipVisible] = useState(false);

    const {
      isOpen: isOwnFarmingDialogOpen,
      handleOpen: openOwnFarmingDialog,
      handleClose: closeOwnFarmingDialog,
    } = useToggle();

    useEffect(() => {
      if (clientSettings?.settings?.frontend) {
        clientSettings.settings.frontend.forEach((item) => {
          if (item.key === OWN_FARMING_MENU_ITEM_KEY) {
            const value = item.value || item.defaultValue;

            setIsOwnFarmingMenuItemShow(value === "true");
          }
        });
      }
    }, [clientSettings]);

    const getSaasPermissions = async () => {
      try {
        const saasAvailableResponse =
          await websiteConstructorApi.api.checkToken();

        const clientId = saasAvailableResponse?.data?.clientId;

        if (clientId) {
          setIsSaasMenuAvailable(true);
        } else {
          setIsSaasMenuAvailable(false);
        }
      } catch (error) {
        setIsSaasMenuAvailable(false);
      }
    };

    useEffect(() => {
      // INFO: Получение clientId, для отображения или скрытия сервисов подписок.

      getSaasPermissions();
    }, []);

    const handleCreateButtonClick = () => {
      if (onMenuItemClick && isOpen) {
        onMenuItemClick();
      }

      navigate(isBankForBanksUser ? "foreign-activity/new" : "/payments/new");
    };

    const handleOwnFarmingMenuItemClick = () => {
      if (isOpen) {
        onMenuItemClick?.();
      }

      openOwnFarmingDialog();
    };

    const xl = useMediaQuery("xl");
    const xxl = useMediaQuery("xxl");
    const isMobile = useMediaQuery("mobile");
    const isTablet = useMediaQuery("tablet");
    const isDesktop = useMediaQuery("desktop");

    const isShortMenu = isTablet && !isOpen;

    const menuClassName = cn(styles.menu, {
      [styles.opened]: isOpen && (isMobile || isTablet),
      [styles["mobile-menu-closed"]]: isMobile && !isOpen,
    });

    const createButtonClassName = cn(styles["create-button"], {
      [styles.short]: isTablet && !isOpen,
    });

    const plusIconClassName = cn(styles["plus-icon"], {
      [styles.disabled]: !isAvailableCreatePermission,
    });

    const createButtonContent = isShortMenu ? (
      <PlusIcon className={plusIconClassName} size="medium" />
    ) : (
      translate("CREATE_BUTTON")
    );

    const translateMenuItem = useCallback(
      (menuItem: MenuItemType) =>
        menuItem.translateCode
          ? translate(menuItem.translateCode)
          : menuItem.name,
      [translate]
    );

    const renderMenuItem = (menuItem: MenuItemType) =>
      menuItem.additionalItems ? (
        <ExpandableMenuItem
          key={menuItem.name}
          name={translateMenuItem(menuItem)}
          path={menuItem.path}
          icon={menuItem.icon}
          disabled={menuItem.disabled}
          dataTestId={menuItem.dataTestId}
          metricName={menuItem.metricName}
          metricData={menuItem.metricData}
          additionalItems={menuItem.additionalItems}
          featureName={menuItem.featureName}
          isMenuOpen={isOpen}
          onMenuItemClick={onMenuItemClick}
        />
      ) : (
        <MenuItem
          key={menuItem.name}
          name={translateMenuItem(menuItem)}
          path={menuItem.path}
          icon={menuItem.icon}
          isMenuOpen={isOpen}
          disabled={menuItem.disabled}
          dataTestId={menuItem.dataTestId}
          metricName={menuItem.metricName}
          metricData={menuItem.metricData}
          featureName={menuItem.featureName}
          onMenuItemClick={onMenuItemClick}
        />
      );

    return (
      <aside data-test-id="menu" className={menuClassName}>
        <Tooltip
          text={translate("TOOLTIP_TEXT")}
          shown={isTooltipVisible}
          placement={isShortMenu ? "bottom-left" : "bottom-center"}
          className={cn(styles.tooltip, {
            [styles["short-menu"]]: isShortMenu,
          })}
        >
          <div
            onPointerEnter={() => {
              setIsTooltipVisible(!isAvailableCreatePermission);
            }}
            onPointerLeave={() => {
              setIsTooltipVisible(false);
            }}
          >
            <Button
              dataTestId="create-button"
              className={createButtonClassName}
              onClick={handleCreateButtonClick}
              data-rptracker-event="click"
              data-rptracker-category="dboMenu"
              data-rptracker-action="menuCreateClick"
              disabled={!isAvailableCreatePermission}
            >
              {createButtonContent}
            </Button>
          </div>
        </Tooltip>
        <div className={styles["nav-container"]}>
          <nav
            data-test-id="menu-block-top"
            className={styles["menu-block-top"]}
          >
            {menuConfig.top.map((menuItem) => renderMenuItem(menuItem))}
          </nav>
          <nav
            data-test-id="menu-block-middle"
            className={styles["menu-block-middle"]}
          >
            {menuConfig.middle.map((menuItem) =>
              renderMenuItem({
                ...menuItem,
                icon: isOpen || isDesktop ? null : menuItem.icon,
              })
            )}
          </nav>
          {isSaasMenuAvailable && (
            <nav
              data-test-id="menu-block-middle"
              className={styles["menu-block-saas"]}
            >
              {menuConfig.saas.map((menuItem) =>
                renderMenuItem({
                  ...menuItem,
                  icon: isOpen || isDesktop ? null : menuItem.icon,
                })
              )}
            </nav>
          )}
          <nav
            data-test-id="menu-block-bottom"
            className={styles["menu-block-bottom"]}
          >
            {menuConfig.bottom.map((menuItem) => renderMenuItem(menuItem))}
            {AuthService.hasSsoRefreshToken && isOwnFarmingMenuItemShow && (
              <div
                className={styles["own-farming-menu-item"]}
                data-rptracker-event="click"
                data-rptracker-category="dboSvoefermerstvo"
                data-rptracker-action="svoefermerstvoClick"
                onClick={handleOwnFarmingMenuItemClick}
              >
                <span className={styles["own-farming-menu-item-content"]}>
                  <OwnFarmingIcon
                    className={styles.icon}
                    size={xl || xxl ? "medium" : "small"}
                  />
                  {(isDesktop || isOpen) && (
                    <Text className={styles.text}>
                      {translate("OWN_FARMING_MENU_ITEM_TEXT")}
                    </Text>
                  )}
                </span>
              </div>
            )}
          </nav>
        </div>
        {createPortal(
          <OwnFarmingDialog
            isOwnFarmingDialogOpen={isOwnFarmingDialogOpen}
            onOwnFarmingDialogClose={closeOwnFarmingDialog}
          />,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          document.getElementById("root")!
        )}
      </aside>
    );
  }
);
